<template>
  <div class="html-editor">
    <div class="mb-4">
      <div class="flex border-b border-gray-200">
        <button
          @click="activeTab = 'editor'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'editor'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]"
        >
          Editor
        </button>
        <button
          @click="activeTab = 'preview'"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'preview'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]"
        >
          Vista Previa
        </button>
      </div>
    </div>

    <!-- Editor Tab -->
    <div v-show="activeTab === 'editor'" class="editor-container">
      <div v-if="editor" class="border border-gray-300 rounded-md overflow-hidden">
        <!-- Toolbar -->
        <div class="flex flex-wrap gap-1 bg-gray-50 border-b border-gray-300 p-2">
          <button
            @click="editor.chain().focus().toggleBold().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('bold') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-medium"
            :disabled="!editor.can().chain().focus().toggleBold().run()"
            title="Bold (Ctrl+B)"
          >
            B
          </button>
          <button
            @click="editor.chain().focus().toggleItalic().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('italic') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-medium italic"
            :disabled="!editor.can().chain().focus().toggleItalic().run()"
            title="Italic (Ctrl+I)"
          >
            I
          </button>
          <button
            @click="editor.chain().focus().toggleUnderline().run()"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-medium underline"
            :disabled="!editor.can().chain().focus().toggleUnderline().run()"
            title="Underline (Ctrl+U)"
          >
            U
          </button>
          <button
            @click="editor.chain().focus().toggleStrike().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('strike') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-medium line-through"
            :disabled="!editor.can().chain().focus().toggleStrike().run()"
            title="Strikethrough"
          >
            S
          </button>

          <div class="w-px bg-gray-300 mx-1"></div>

          <button
            @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('heading', { level: 1 }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-bold"
            title="Heading 1"
          >
            H1
          </button>
          <button
            @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('heading', { level: 2 }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-bold"
            title="Heading 2"
          >
            H2
          </button>
          <button
            @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('heading', { level: 3 }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-bold"
            title="Heading 3"
          >
            H3
          </button>

          <div class="w-px bg-gray-300 mx-1"></div>

          <button
            @click="editor.chain().focus().toggleBulletList().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('bulletList') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Bullet List"
          >
            • List
          </button>
          <button
            @click="editor.chain().focus().toggleOrderedList().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('orderedList') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Ordered List"
          >
            1. List
          </button>
          <button
            @click="editor.chain().focus().toggleBlockquote().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('blockquote') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Blockquote"
          >
            "
          </button>
          <button
            @click="editor.chain().focus().toggleCodeBlock().run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive('codeBlock') }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm font-mono"
            title="Code Block"
          >
            &lt;/&gt;
          </button>

          <div class="w-px bg-gray-300 mx-1"></div>

          <button
            @click="editor.chain().focus().setTextAlign('left').run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive({ textAlign: 'left' }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Align Left"
          >
            ←
          </button>
          <button
            @click="editor.chain().focus().setTextAlign('center').run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive({ textAlign: 'center' }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Align Center"
          >
            ↔
          </button>
          <button
            @click="editor.chain().focus().setTextAlign('right').run()"
            :class="{ 'bg-blue-500 text-white': editor.isActive({ textAlign: 'right' }) }"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Align Right"
          >
            →
          </button>

          <div class="w-px bg-gray-300 mx-1"></div>

          <button
            @click="editor.chain().focus().clearNodes().run()"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            title="Clear Formatting"
          >
            Clear
          </button>
          <button
            @click="editor.chain().focus().undo().run()"
            :disabled="!editor.can().undo()"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
            title="Undo (Ctrl+Z)"
          >
            ↶
          </button>
          <button
            @click="editor.chain().focus().redo().run()"
            :disabled="!editor.can().redo()"
            class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
            title="Redo (Ctrl+Y)"
          >
            ↷
          </button>
        </div>

        <!-- Editor -->
        <EditorContent :editor="editor" :style="{ minHeight: editorHeight }" />
      </div>
    </div>

    <!-- Preview Tab -->
    <div v-show="activeTab === 'preview'" class="preview-container">
      <div 
        class="border border-gray-300 rounded-md p-4 bg-white min-h-[400px] prose max-w-none"
        v-html="sanitizedPreview"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, computed } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';

interface Props {
  modelValue: string;
  placeholder?: string;
  height?: string;
  readonly?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Escriba su contenido aquí...',
  height: '400px',
  readonly: false
});

const emit = defineEmits<Emits>();

const activeTab = ref<'editor' | 'preview'>('editor');
const editorHeight = ref(props.height);

const sanitizeHtml = (value: string): string => {
  return value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
}

const sanitizedPreview = computed(() => sanitizeHtml(props.modelValue || ''))

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit.configure({
      codeBlock: false,
      code: false
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph']
    }),
    Underline
  ],
  onUpdate: ({ editor: editorInstance }) => {
    emit('update:modelValue', editorInstance.getHTML());
  },
  editable: !props.readonly
});

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<style scoped>
.html-editor {
  @apply w-full;
}

.editor-container :deep(.ProseMirror) {
  min-height: 350px;
  font-size: 14px;
  line-height: 1.6;
  padding: 12px;
  outline: none;
  
  &:focus {
    outline: 1px solid #e5e7eb;
  }

  & > * + * {
    margin-top: 0.75em;
  }

  & blockquote {
    padding-left: 1rem;
    border-left: 2px solid #e5e7eb;
    margin-left: 0;
    color: #6b7280;
    font-style: italic;
  }

  & code {
    background-color: #f3f4f6;
    color: #dc2626;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
    font-family: 'Courier New', monospace;
  }

  & pre {
    background-color: #1f2937;
    color: #f9fafb;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;

    & code {
      background-color: transparent;
      color: inherit;
      padding: 0;
      border-radius: 0;
      font-size: 0.875em;
    }
  }

  & ul {
    list-style: disc;
    padding-left: 1.5rem;
    margin: 0.75rem 0;
  }

  & ol {
    list-style: decimal;
    padding-left: 1.5rem;
    margin: 0.75rem 0;
  }

  & li {
    margin-bottom: 0.25rem;
  }

  & h1 {
    font-size: 2em;
    font-weight: bold;
    margin: 0.67em 0;
  }

  & h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin: 0.75em 0;
  }

  & h3 {
    font-size: 1.25em;
    font-weight: bold;
    margin: 0.83em 0;
  }

  & h4,
  & h5,
  & h6 {
    font-weight: bold;
    margin: 1em 0;
  }
}

.preview-container {
  @apply w-full;
}

/* Estilos para la vista previa */
.prose {
  @apply text-gray-900;
}

.prose h1 {
  @apply text-2xl font-bold mb-4 mt-6;
}

.prose h2 {
  @apply text-xl font-bold mb-3 mt-5;
}

.prose h3 {
  @apply text-lg font-bold mb-2 mt-4;
}

.prose h4 {
  @apply text-base font-bold mb-2 mt-3;
}

.prose h5 {
  @apply text-sm font-bold mb-1 mt-2;
}

.prose h6 {
  @apply text-xs font-bold mb-1 mt-2;
}

.prose p {
  @apply mb-3 leading-relaxed;
}

.prose ul {
  @apply list-disc list-inside mb-3;
}

.prose ol {
  @apply list-decimal list-inside mb-3;
}

.prose li {
  @apply mb-1;
}

.prose blockquote {
  @apply border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-3;
}

.prose strong {
  @apply font-bold;
}

.prose em {
  @apply italic;
}

.prose u {
  @apply underline;
}

.prose a {
  @apply text-blue-600 hover:text-blue-800 underline;
}

.prose code {
  @apply bg-gray-100 px-1 py-0.5 rounded text-sm font-mono;
}

.prose pre {
  @apply bg-gray-100 p-3 rounded overflow-x-auto mb-3;
}

.prose pre code {
  @apply bg-transparent p-0;
}
</style>